import { createSlice } from "@reduxjs/toolkit";

import type { Marker, ResponsePayload } from "@/types";
import { fetchPaginatedPins, fetchPinDetails } from "./thunk";

export interface MarkersInitialState {
  markers: Marker[];
  status: "idle" | "fetching" | "error" | "paginatingPins";
  error: string;
  hasPageReset: boolean;
  filteredCategoriesData: {
    categories: string[];
    isSelfPin: boolean;
    sortOrder: string;
  };
  meta: {
    count: number;
    totalMarkersCount: number;
  };
  webPinDetails: any;
  prevMarkers: any;
}

const initialState: MarkersInitialState = {
  markers: [],
  webPinDetails: [],
  prevMarkers: [],
  hasPageReset: false,
  filteredCategoriesData: {
    categories: [],
    isSelfPin: false,
    sortOrder: "newest"
  },
  meta: {
    count: 0,
    totalMarkersCount: 0
  },
  status: "idle",
  error: ""
};

export const placeMarkersSlice = createSlice({
  name: "placeMarkersSlice",
  initialState,
  reducers: {
    setMarkers: (state, action) => {
      state.markers = action.payload;
    },
    updateMarker: (state, action) => {
      const marker = state.markers.find((m) => m.id === action.payload.id);
      if (marker) {
        marker.lat = action.payload.lat;
        marker.lng = action.payload.lng;
        marker.address = action.payload.address;
      }
    },
    setFilteredCategoriesData: (state, action) => {
      state.filteredCategoriesData = action.payload;
    },
    resetMarkers: (state) => {
      state.markers = initialState.markers;
      state.filteredCategoriesData = initialState.filteredCategoriesData;
    },
    resetFilterCategories: (state) => {
      state.filteredCategoriesData = initialState.filteredCategoriesData;
    },
    setPreviousMarker: (state, action) => {
      state.prevMarkers = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPinDetails.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchPinDetails.fulfilled, (state, action) => {
        state.markers = action.payload.data ?? [];
        state.meta = action.payload.meta;
        state.hasPageReset = true;
        state.status = "idle";
      })
      .addCase(fetchPinDetails.rejected, (state, action) => {
        const { statusText = "" } = (action.payload as ResponsePayload) || {};

        state.status = action.payload === "aborted" ? "fetching" : "idle";
        state.error = statusText;
      })
      .addCase(fetchPaginatedPins.pending, (state) => {
        state.status = "paginatingPins";
      })
      .addCase(fetchPaginatedPins.fulfilled, (state, action) => {
        state.markers.push(...(action.payload.data ?? []));
        state.meta = action.payload.meta;
        state.hasPageReset = false;
        state.status = "idle";
      })
      .addCase(fetchPaginatedPins.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.error = statusText;
      });
  }
});

export const {
  setMarkers,
  setFilteredCategoriesData,
  resetFilterCategories,
  resetMarkers,
  updateMarker,
  setPreviousMarker
} = placeMarkersSlice.actions;
